import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AssistantProfile,
  AuditEvent,
  ConfigSnapshot,
  DidInventoryItem,
  DidStatus,
  ManualStatus,
  TestResult,
  generateDidRange,
  generateManualConfig,
  seedDidItem
} from "../domain";
import "./styles.css";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
const cognitoClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
const redirectUri =
  (import.meta.env.VITE_COGNITO_REDIRECT_URI as string | undefined) || window.location.origin;
const isDevPreview = import.meta.env.DEV && !apiBaseUrl;

const statusOptions: DidStatus[] = [
  "available",
  "reserved",
  "assigned",
  "configured",
  "testing",
  "live",
  "retired",
  "blocked"
];
const manualStatusOptions: ManualStatus[] = ["not_started", "manual_pending", "configured", "verified"];
const testResultOptions: TestResult[] = ["not_tested", "greeting_only", "two_way_audio_ok", "failed"];
const assistantTypeOptions: AssistantProfile["type"][] = [
  "meydan_operator",
  "customer_pa",
  "vendor_pa",
  "custom"
];
const productTemplates: Record<
  string,
  Pick<AssistantProfile, "type" | "display_name" | "owner" | "notes">
> = {
  meydan_operator: {
    type: "meydan_operator",
    display_name: "Meydan AI - Operator",
    owner: "Meydan AI",
    notes: "Default Meydan operator assistant."
  },
  vendor_pa: {
    type: "vendor_pa",
    display_name: "Vendor Personal Assistant",
    owner: "Vendor",
    notes: "Vendor/customer PA. Agent ID must be set per buyer."
  },
  customer_pa: {
    type: "customer_pa",
    display_name: "Customer Personal Assistant",
    owner: "Customer",
    notes: "Customer-owned PA. Agent ID must be set per buyer."
  },
  custom: {
    type: "custom",
    display_name: "Custom Assistant",
    owner: "Custom",
    notes: "Custom routing profile."
  }
};

type HandoffProvider = "elevenlabs" | "vapi" | "generic_sip";

const handoffProviderOptions: Array<{ value: HandoffProvider; label: string }> = [
  { value: "elevenlabs", label: "ElevenLabs" },
  { value: "vapi", label: "Vapi BYO SIP" },
  { value: "generic_sip", label: "Generic SIP / other" }
];

type AssignmentForm = {
  status: DidStatus;
  company_name: string;
  customer_name: string;
  customer_email: string;
  crm_contact_id: string;
  customer_notes: string;
  agent_id: string;
  assistant_type: AssistantProfile["type"];
  assistant_display_name: string;
  assistant_owner: string;
  assistant_notes: string;
  vendor_name: string;
  vendor_contact_email: string;
  pa_dashboard_customer_id: string;
  elevenlabs_account_name: string;
  elevenlabs_account_owner_email: string;
  handoff_notes: string;
  current_provider: "twilio" | "elevenlabs_sip" | "other" | "";
  elevenlabs_phone_number_id: string;
  elevenlabs_status: ManualStatus;
  last_test_result: TestResult;
  notes: string;
};

const previewRows: DidInventoryItem[] = generateDidRange().map((row) => {
  const item = seedDidItem(row.did, "preview");
  if (row.did === "047777600") {
    return {
      ...item,
      status: "assigned",
      assigned_user: {
        name: "Vendor PA Customer",
        email: "customer@example.com",
        notes: "Example customer number for PA dashboard handoff."
      },
      assigned_agent_id: "agent_vendor_customer_pa",
      assistant_profile: {
        type: "vendor_pa",
        display_name: "Customer Personal Assistant",
        owner: "PA Vendor"
      },
      vendor_profile: {
        vendor_name: "PA Vendor",
        contact_email: "vendor@example.com",
        pa_dashboard_customer_id: "customer-123",
        current_provider: "twilio",
        target_provider: "elevenlabs_sip"
      }
    };
  }
  return item;
});

function App() {
  const [idToken, setIdToken] = useState(() => getIdToken());
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<DidStatus | "all">("all");
  const [profileFilter, setProfileFilter] = useState<AssistantProfile["type"] | "all">("all");
  const [testFilter, setTestFilter] = useState<TestResult | "all">("all");
  const [selectedDid, setSelectedDid] = useState("047777666");
  const [rows, setRows] = useState<DidInventoryItem[]>(isDevPreview ? previewRows : []);
  const [apiStatus, setApiStatus] = useState<string>(
    isDevPreview ? "Development preview data loaded" : "Sign in required"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [auditRows, setAuditRows] = useState<AuditEvent[]>([]);
  const [isAssignmentOpen, setIsAssignmentOpen] = useState(false);
  const [handoffProvider, setHandoffProvider] = useState<HandoffProvider>("elevenlabs");
  const [handoffDids, setHandoffDids] = useState<string[]>([]);

  const apiFetch = React.useCallback(
    async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
      if (!apiBaseUrl || !idToken) throw new Error("Sign in is required.");
      const response = await fetch(`${apiBaseUrl.replace(/\/$/, "")}${path}`, {
        ...init,
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${idToken}`,
          ...(init.headers || {})
        }
      });
      if (response.status === 401) {
        clearStoredToken();
        setIdToken(null);
        setRows([]);
        throw new Error("Session expired. Please sign in again.");
      }
      const body = (await response.json().catch(() => ({}))) as T & { error?: { message?: string } };
      if (!response.ok) throw new Error(body.error?.message || `API ${response.status}`);
      return body;
    },
    [idToken]
  );

  const loadRows = React.useCallback(async () => {
    if (isDevPreview || !idToken) return;
    setIsLoading(true);
    setMessage(null);
    try {
      const body = await apiFetch<{ items: DidInventoryItem[] }>("/api/dids");
      setRows(body.items);
      setApiStatus("Connected to AWS DID backend");
      if (!body.items.some((item) => item.did === selectedDid)) {
        setSelectedDid(body.items.find((item) => item.protection === "golden_reference")?.did || body.items[0]?.did || "");
      }
    } catch (err) {
      setApiStatus(`AWS API not connected: ${err instanceof Error ? err.message : err}`);
    } finally {
      setIsLoading(false);
    }
  }, [apiFetch, idToken, selectedDid]);

  React.useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const selected = rows.find((row) => row.did === selectedDid) || rows[0] || null;
  const selectedHandoffRows = handoffDids
    .map((did) => rows.find((row) => row.did === did))
    .filter((row): row is DidInventoryItem => Boolean(row));
  const handoffRows = selectedHandoffRows.length > 0 ? selectedHandoffRows : selected ? [selected] : [];

  const [form, setForm] = useState<AssignmentForm>(() => formFromItem(selected || previewRows[66]!));

  React.useEffect(() => {
    if (selected) setForm(formFromItem(selected));
  }, [selected?.did]);

  function openAssignment(row: DidInventoryItem) {
    setSelectedDid(row.did);
    setForm(formFromItem(row));
    setMessage(null);
    setIsAssignmentOpen(true);
  }

  React.useEffect(() => {
    if (!selected || isDevPreview || !idToken) {
      setAuditRows([]);
      return;
    }
    apiFetch<{ items: AuditEvent[] }>(`/api/dids/${selected.did}/audit`)
      .then((body) => setAuditRows(body.items))
      .catch(() => setAuditRows([]));
  }, [apiFetch, idToken, selected?.did]);

  const filteredRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (profileFilter !== "all" && row.assistant_profile?.type !== profileFilter) return false;
      if (testFilter !== "all" && row.last_test_result !== testFilter) return false;
      if (!needle) return true;
      return [
        row.did,
        row.e164,
        row.status,
        row.assigned_user?.company_name,
        row.assigned_user?.email,
        row.assigned_user?.name,
        row.assigned_agent_id,
        row.assistant_profile?.type,
        row.vendor_profile?.vendor_name
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [profileFilter, query, rows, statusFilter, testFilter]);

  const counts = useMemo(
    () => ({
      available: rows.filter((row) => row.status === "available").length,
      reserved: rows.filter((row) => row.status === "reserved").length,
      assigned: rows.filter((row) => row.status === "assigned").length,
      live: rows.filter((row) => row.status === "live").length,
      blocked: rows.filter((row) => row.status === "blocked").length
    }),
    [rows]
  );

  const snapshot = selected ? selected.config_snapshot || generateManualConfig(selected) : null;

  function toggleHandoffDid(did: string) {
    setHandoffDids((current) =>
      current.includes(did) ? current.filter((value) => value !== did) : [...current, did].sort()
    );
  }

  function selectVisibleForHandoff() {
    setHandoffDids(filteredRows.map((row) => row.did).sort());
  }

  async function saveAssignment() {
    if (!selected) return;
    setIsSaving(true);
    setMessage(null);
    try {
      const patch = patchFromForm(form);
      const body = await apiFetch<{ item: DidInventoryItem }>(`/api/dids/${selected.did}`, {
        method: "PATCH",
        body: JSON.stringify(patch)
      });
      setRows((current) => current.map((item) => (item.did === body.item.did ? body.item : item)));
      setSelectedDid(body.item.did);
      setIsAssignmentOpen(false);
      setMessage(`Saved ${body.item.did}.`);
      const audit = await apiFetch<{ items: AuditEvent[] }>(`/api/dids/${body.item.did}/audit`);
      setAuditRows(audit.items);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setIsSaving(false);
    }
  }

  async function generateConfig() {
    if (!selected) return;
    setIsGenerating(true);
    setMessage(null);
    try {
      const body = await apiFetch<{ config_snapshot: ConfigSnapshot }>(
        `/api/dids/${selected.did}/generate-config`,
        { method: "POST" }
      );
      setRows((current) =>
        current.map((item) =>
          item.did === selected.did
            ? {
                ...item,
                config_snapshot: body.config_snapshot,
                updated_at: body.config_snapshot.generated_at
              }
            : item
        )
      );
      setMessage(`Generated config for ${selected.did}.`);
      const audit = await apiFetch<{ items: AuditEvent[] }>(`/api/dids/${selected.did}/audit`);
      setAuditRows(audit.items);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Config generation failed.");
    } finally {
      setIsGenerating(false);
    }
  }

  function signOut() {
    clearStoredToken();
    setIdToken(null);
    setRows([]);
    setAuditRows([]);
    setApiStatus("Signed out");
  }

  if (!idToken && !isDevPreview) {
    return (
      <main className="shell authShell">
        <section className="authCard">
          <p className="eyebrow">Meydan Calls Console</p>
          <h1>DID provisioning ledger</h1>
          <p>
            Sign in to view, assign, and provision DID numbers. Inventory data is hidden until
            Cognito authentication succeeds.
          </p>
          <button className="loginButton" onClick={startLogin}>
            Sign in
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="shell">
      <header className="hero">
        <div>
          <p className="eyebrow">Meydan Calls Console</p>
          <h1>DID inventory</h1>
          <p className="heroText">
            Manage numbers from 047777600 to 047777699, assign them to customers,
            and prepare vendor handoffs for inbound or outbound AI voice platforms.
          </p>
        </div>
        <div className="heroActions">
          <span className="connectionState">{apiStatus}</span>
          <button className="secondaryButton" onClick={() => void loadRows()} disabled={isLoading}>
            {isLoading ? "Refreshing..." : "Refresh"}
          </button>
          {!isDevPreview ? (
            <button className="secondaryButton" onClick={signOut}>
              Sign out
            </button>
          ) : null}
        </div>
      </header>

      <section className="metrics">
        <Metric label="Available" value={counts.available} />
        <Metric label="Reserved" value={counts.reserved} />
        <Metric label="Assigned" value={counts.assigned} />
        <Metric label="Live" value={counts.live} />
        <Metric label="Blocked" value={counts.blocked} />
      </section>

      <section className="workspace">
        <div className="panel tablePanel">
          <div className="panelHeader">
            <h2>DID inventory</h2>
            <div className="filters">
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search DID, company, agent..."
              />
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as DidStatus | "all")}>
                <option value="all">All statuses</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {label(status)}
                  </option>
                ))}
              </select>
              <select
                value={profileFilter}
                onChange={(event) => setProfileFilter(event.target.value as AssistantProfile["type"] | "all")}
              >
                <option value="all">All profiles</option>
                {assistantTypeOptions.map((type) => (
                  <option key={type} value={type}>
                    {label(type)}
                  </option>
                ))}
              </select>
              <select value={testFilter} onChange={(event) => setTestFilter(event.target.value as TestResult | "all")}>
                <option value="all">All tests</option>
                {testResultOptions.map((result) => (
                  <option key={result} value={result}>
                    {label(result)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="bulkHandoffBar">
            <div>
              <strong>{handoffRows.length} number{handoffRows.length === 1 ? "" : "s"} in handoff package</strong>
              <span>{selectedHandoffRows.length > 0 ? "Using checked rows" : "Using selected row"}</span>
            </div>
            <div className="bulkHandoffActions">
              <select value={handoffProvider} onChange={(event) => setHandoffProvider(event.target.value as HandoffProvider)}>
                {handoffProviderOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <button className="secondaryButton" type="button" onClick={selectVisibleForHandoff}>
                Select visible
              </button>
              <button className="secondaryButton" type="button" onClick={() => setHandoffDids([])}>
                Clear
              </button>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Select</th>
                <th>DID</th>
                <th>E.164</th>
                <th>Status</th>
                <th>Company</th>
                <th>Vendor</th>
                <th>Profile</th>
                <th>Test</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.did}
                  className={row.did === selected?.did ? "selected" : ""}
                  onClick={() => openAssignment(row)}
                >
                  <td>
                    <input
                      className="rowCheckbox"
                      type="checkbox"
                      aria-label={`Select ${row.did} for handoff`}
                      checked={handoffDids.includes(row.did)}
                      onChange={(event) => {
                        event.stopPropagation();
                        toggleHandoffDid(row.did);
                      }}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </td>
                  <td>
                    <strong>{row.did}</strong>
                    {row.protection === "golden_reference" ? <span className="mini">locked</span> : null}
                  </td>
                  <td>{row.e164}</td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td>{row.assigned_user?.company_name || row.assigned_user?.name || "Unassigned"}</td>
                  <td>{row.vendor_profile?.vendor_name || "N/A"}</td>
                  <td>{row.assistant_profile?.type ? label(row.assistant_profile.type) : "Not set"}</td>
                  <td>{row.last_test_result.replaceAll("_", " ")}</td>
                  <td>{formatDate(row.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredRows.length === 0 ? <p className="emptyState">No DIDs match the current filters.</p> : null}
        </div>

      </section>

      {selected ? (
        <SelectedDidSummary
          selected={selected}
          snapshot={snapshot}
          auditRows={auditRows}
          isGenerating={isGenerating}
          message={message}
          handoffRows={handoffRows}
          handoffProvider={handoffProvider}
          onProviderChange={setHandoffProvider}
          onEdit={() => openAssignment(selected)}
          onGenerate={() => void generateConfig()}
        />
      ) : null}

      {selected && isAssignmentOpen ? (
        <AssignmentModal
          selected={selected}
          form={form}
          isSaving={isSaving}
          message={message}
          onClose={() => setIsAssignmentOpen(false)}
          onSave={() => void saveAssignment()}
          onTemplate={(template) => setForm((current) => applyTemplate(current, template))}
          onChange={setFormValue}
        />
      ) : null}
    </main>
  );

  function setFormValue<K extends keyof AssignmentForm>(key: K, value: AssignmentForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }
}

function startLogin() {
  if (!cognitoDomain || !cognitoClientId) return;
  const normalizedCognitoDomain = cognitoDomain.replace(/^https?:\/\//, "").replace(/\/+$/, "");
  const params = new URLSearchParams({
    client_id: cognitoClientId,
    response_type: "token",
    scope: "openid email profile",
    redirect_uri: redirectUri
  });
  window.location.href = `https://${normalizedCognitoDomain}/oauth2/authorize?${params.toString()}`;
}

function getIdToken(): string | null {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = hash.get("id_token");
  if (token) {
    window.localStorage.setItem("calls-console-id-token", token);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    return token;
  }
  return window.localStorage.getItem("calls-console-id-token");
}

function clearStoredToken() {
  window.localStorage.removeItem("calls-console-id-token");
}

function StatusBadge({ status }: { status: DidInventoryItem["status"] }) {
  return <span className={`badge ${status}`}>{status.replaceAll("_", " ")}</span>;
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Field({ label: fieldLabel, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <label className={wide ? "field wide" : "field"}>
      <span>{fieldLabel}</span>
      {children}
    </label>
  );
}

function CopyButton({ value, label: buttonLabel = "Copy JSON" }: { value: string; label?: string }) {
  return (
    <button
      className="secondaryButton copyButton"
      type="button"
      onClick={() => void navigator.clipboard?.writeText(value)}
    >
      {buttonLabel}
    </button>
  );
}

function SelectedDidSummary({
  selected,
  snapshot,
  auditRows,
  isGenerating,
  message,
  handoffRows,
  handoffProvider,
  onProviderChange,
  onEdit,
  onGenerate
}: {
  selected: DidInventoryItem;
  snapshot: ConfigSnapshot | null;
  auditRows: AuditEvent[];
  isGenerating: boolean;
  message: string | null;
  handoffRows: DidInventoryItem[];
  handoffProvider: HandoffProvider;
  onProviderChange: (provider: HandoffProvider) => void;
  onEdit: () => void;
  onGenerate: () => void;
}) {
  return (
    <section className="handoffWorkspace">
      <div className="panel summaryPanel">
        <div className="panelHeader">
          <div>
            <p className="eyebrow">Selected DID</p>
            <h2>{selected.e164}</h2>
          </div>
          <StatusBadge status={selected.status} />
        </div>

        {selected.protection === "golden_reference" ? (
          <div className="notice">Golden reference is locked. Use it as the known-good baseline.</div>
        ) : null}

        <section className="detailSection">
          <dl>
            <div>
              <dt>Company</dt>
              <dd>{selected.assigned_user?.company_name || "Not assigned"}</dd>
            </div>
            <div>
              <dt>Customer</dt>
              <dd>{selected.assigned_user?.name || "Not assigned"}</dd>
            </div>
            <div>
              <dt>Vendor</dt>
              <dd>{selected.vendor_profile?.vendor_name || "N/A"}</dd>
            </div>
            <div>
              <dt>Profile</dt>
              <dd>{selected.assistant_profile?.type ? label(selected.assistant_profile.type) : "Not set"}</dd>
            </div>
            <div>
              <dt>Agent ID</dt>
              <dd>{selected.assigned_agent_id || "Not set"}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{formatDate(selected.updated_at)} by {selected.updated_by}</dd>
            </div>
          </dl>
        </section>

        <section className="detailSection">
          <h3>Readiness</h3>
          <ul className="checklist">
            <li><span>PBX dial pattern</span><strong>{label(selected.pbx_dial_pattern_status)}</strong></li>
            <li><span>PBX route</span><strong>{label(selected.pbx_route_status)}</strong></li>
            <li><span>ElevenLabs</span><strong>{label(selected.elevenlabs_status)}</strong></li>
            <li><span>Call test</span><strong>{label(selected.last_test_result)}</strong></li>
          </ul>
        </section>

        <div className="detailActions">
          <button
            className="loginButton"
            type="button"
            disabled={selected.protection === "golden_reference"}
            onClick={onEdit}
          >
            Edit assignment
          </button>
          <button className="secondaryButton" type="button" disabled={isGenerating} onClick={onGenerate}>
            {isGenerating ? "Generating..." : "Generate handoff"}
          </button>
        </div>
        {message ? <p className="statusMessage">{message}</p> : null}
      </div>

      <HandoffPanel
        selected={selected}
        snapshot={snapshot}
        rows={handoffRows}
        provider={handoffProvider}
        onProviderChange={onProviderChange}
      />

      <div className="panel auditPanel">
        <div className="panelHeader compact">
          <h2>Audit history</h2>
        </div>
        <section className="detailSection">
          {auditRows.length === 0 ? (
            <p className="muted">No audit events loaded for this DID.</p>
          ) : (
            <ul className="auditList">
              {auditRows.slice(0, 8).map((event) => (
                <li key={event.audit_id}>
                  <strong>{event.event_type.replaceAll("_", " ")}</strong>
                  <span>{formatDate(event.created_at)} by {event.actor}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </section>
  );
}

function HandoffPanel({
  selected,
  snapshot,
  rows,
  provider,
  onProviderChange
}: {
  selected: DidInventoryItem;
  snapshot: ConfigSnapshot | null;
  rows: DidInventoryItem[];
  provider: HandoffProvider;
  onProviderChange: (provider: HandoffProvider) => void;
}) {
  const packageRows = rows.length > 0 ? rows : [selected];
  const packageData = buildHandoffPackage(packageRows, provider);
  const providerCopy = providerCopyFor(provider);
  const representativeSnapshot = snapshot || generateManualConfig(selected);
  const inbound = representativeSnapshot.elevenlabs.inbound_trunk_config;
  const outbound = representativeSnapshot.elevenlabs.outbound_trunk_config;
  const handoffJson = JSON.stringify(packageData, null, 2);
  const vendorInstructions = buildVendorInstructions(packageRows, provider);

  return (
    <div className="panel handoffPanel">
      <div className="panelHeader">
        <div>
          <p className="eyebrow">Handoff</p>
          <h2>{packageRows.length} number{packageRows.length === 1 ? "" : "s"} for {providerCopy.label}</h2>
        </div>
        <div className="handoffHeaderActions">
          <select value={provider} onChange={(event) => onProviderChange(event.target.value as HandoffProvider)}>
            {handoffProviderOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <CopyButton value={handoffJson} label="Copy package JSON" />
        </div>
      </div>

      <section className="detailSection">
        <div className="handoffIdentity">
          <div>
            <dt>Numbers</dt>
            <dd>{packageRows.map((row) => row.did).join(", ")}</dd>
          </div>
          <div>
            <dt>E.164</dt>
            <dd>{packageRows.map((row) => row.e164).join(", ")}</dd>
          </div>
          <div>
            <dt>Vendor account</dt>
            <dd>{firstValue(packageRows, (row) => row.vendor_profile?.elevenlabs_account_name || row.vendor_profile?.vendor_name) || "Not set"}</dd>
          </div>
          <div>
            <dt>Owner email</dt>
            <dd>{firstValue(packageRows, (row) => row.vendor_profile?.elevenlabs_account_owner_email || row.vendor_profile?.contact_email) || "Not set"}</dd>
          </div>
        </div>
      </section>

      <section className="detailSection">
        <h3>What this handoff means</h3>
        <p className="muted">{providerCopy.summary}</p>
      </section>

      <section className="handoffCards">
        <article className="handoffCard">
          <span className="handoffLabel">Inbound</span>
          <h3>{providerCopy.inboundTitle}</h3>
          <dl>
            <div><dt>Numbers</dt><dd>{packageRows.map((row) => row.e164).join(", ")}</dd></div>
            <div><dt>Meydan source IP</dt><dd>{inbound.allowed_addresses.join(", ")}</dd></div>
            <div><dt>Vendor requirement</dt><dd>{providerCopy.inboundRequirement}</dd></div>
            <div><dt>Media encryption</dt><dd>{inbound.media_encryption}</dd></div>
          </dl>
          <CopyButton value={JSON.stringify(packageData.inbound, null, 2)} label="Copy inbound" />
        </article>

        <article className="handoffCard">
          <span className="handoffLabel">Outbound</span>
          <h3>{providerCopy.outboundTitle}</h3>
          <dl>
            <div><dt>Gateway</dt><dd>{outbound.address}:5061</dd></div>
            <div><dt>Transport</dt><dd>{outbound.transport}</dd></div>
            <div><dt>Vendor requirement</dt><dd>{providerCopy.outboundRequirement}</dd></div>
            <div><dt>Media encryption</dt><dd>{outbound.media_encryption}</dd></div>
          </dl>
          <CopyButton value={JSON.stringify(packageData.outbound, null, 2)} label="Copy outbound" />
        </article>
      </section>

      <section className="detailSection">
        <h3>Vendor instructions</h3>
        <p className="muted">
          {providerCopy.note}
        </p>
        <CopyButton value={vendorInstructions} label="Copy vendor instructions" />
      </section>
    </div>
  );
}

function AssignmentModal({
  selected,
  form,
  isSaving,
  message,
  onClose,
  onSave,
  onTemplate,
  onChange
}: {
  selected: DidInventoryItem;
  form: AssignmentForm;
  isSaving: boolean;
  message: string | null;
  onClose: () => void;
  onSave: () => void;
  onTemplate: (template: string) => void;
  onChange: <K extends keyof AssignmentForm>(key: K, value: AssignmentForm[K]) => void;
}) {
  const isLocked = selected.protection === "golden_reference";
  const coreDisabled = isLocked;

  return (
    <div className="modalBackdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="assignmentModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assignment-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modalHeader">
          <div>
            <p className="eyebrow">Assign DID</p>
            <h2 id="assignment-modal-title">{selected.e164}</h2>
          </div>
          <button className="iconButton" type="button" aria-label="Close assignment modal" onClick={onClose}>
            ×
          </button>
        </div>

        {isLocked ? (
          <div className="notice">
            Golden reference routing is locked. Use it as the known-good baseline.
          </div>
        ) : null}

        <div className="templateRow modalTemplates">
          {Object.keys(productTemplates).map((template) => (
            <button
              key={template}
              className="secondaryButton"
              type="button"
              disabled={isLocked}
              onClick={() => onTemplate(template)}
            >
              {label(template)}
            </button>
          ))}
        </div>

        <div className="formGrid modalGrid">
          <Field label="Status">
            <select disabled={coreDisabled} value={form.status} onChange={(event) => onChange("status", event.target.value as DidStatus)}>
              {statusOptions.map((status) => (
                <option key={status} value={status}>{label(status)}</option>
              ))}
            </select>
          </Field>
          <Field label="Company">
            <input disabled={coreDisabled} value={form.company_name} onChange={(event) => onChange("company_name", event.target.value)} />
          </Field>
          <Field label="Customer name">
            <input disabled={coreDisabled} value={form.customer_name} onChange={(event) => onChange("customer_name", event.target.value)} />
          </Field>
          <Field label="Customer email">
            <input disabled={coreDisabled} value={form.customer_email} onChange={(event) => onChange("customer_email", event.target.value)} />
          </Field>
          <Field label="Vendor">
            <input disabled={coreDisabled} value={form.vendor_name} onChange={(event) => onChange("vendor_name", event.target.value)} />
          </Field>
          <Field label="Vendor email">
            <input disabled={coreDisabled} value={form.vendor_contact_email} onChange={(event) => onChange("vendor_contact_email", event.target.value)} />
          </Field>
          <Field label="ElevenLabs account">
            <input disabled={coreDisabled} value={form.elevenlabs_account_name} onChange={(event) => onChange("elevenlabs_account_name", event.target.value)} />
          </Field>
          <Field label="Account owner email">
            <input disabled={coreDisabled} value={form.elevenlabs_account_owner_email} onChange={(event) => onChange("elevenlabs_account_owner_email", event.target.value)} />
          </Field>
          <Field label="ElevenLabs agent ID">
            <input disabled={coreDisabled} value={form.agent_id} onChange={(event) => onChange("agent_id", event.target.value)} />
          </Field>
          <Field label="Phone number ID">
            <input disabled={coreDisabled} value={form.elevenlabs_phone_number_id} onChange={(event) => onChange("elevenlabs_phone_number_id", event.target.value)} />
          </Field>
          <Field label="Assistant profile">
            <select
              disabled={coreDisabled}
              value={form.assistant_type}
              onChange={(event) => onChange("assistant_type", event.target.value as AssistantProfile["type"])}
            >
              {assistantTypeOptions.map((type) => (
                <option key={type} value={type}>{label(type)}</option>
              ))}
            </select>
          </Field>
          <Field label="Current provider">
            <select
              disabled={coreDisabled}
              value={form.current_provider}
              onChange={(event) => onChange("current_provider", event.target.value as AssignmentForm["current_provider"])}
            >
              <option value="">N/A</option>
              <option value="twilio">Twilio</option>
              <option value="elevenlabs_sip">ElevenLabs SIP</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="ElevenLabs status">
            <select disabled={coreDisabled} value={form.elevenlabs_status} onChange={(event) => onChange("elevenlabs_status", event.target.value as ManualStatus)}>
              {manualStatusOptions.map((status) => (
                <option key={status} value={status}>{label(status)}</option>
              ))}
            </select>
          </Field>
          <Field label="Call test">
            <select disabled={coreDisabled} value={form.last_test_result} onChange={(event) => onChange("last_test_result", event.target.value as TestResult)}>
              {testResultOptions.map((result) => (
                <option key={result} value={result}>{label(result)}</option>
              ))}
            </select>
          </Field>
          <Field label="Handoff notes" wide>
            <textarea disabled={coreDisabled} value={form.handoff_notes} onChange={(event) => onChange("handoff_notes", event.target.value)} />
          </Field>
          <Field label="Internal notes" wide>
            <textarea disabled={coreDisabled} value={form.notes} onChange={(event) => onChange("notes", event.target.value)} />
          </Field>
        </div>

        <div className="modalFooter">
          {message ? <p className="statusMessage">{message}</p> : <span />}
          <div className="actionRow">
            <button className="secondaryButton" type="button" onClick={onClose}>
              Cancel
            </button>
            <button className="loginButton" type="button" disabled={isSaving} onClick={onSave}>
              {isSaving ? "Saving..." : "Save assignment"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function providerCopyFor(provider: HandoffProvider) {
  if (provider === "vapi") {
    return {
      label: "Vapi BYO SIP",
      summary:
        "Use this when the vendor runs assistants on Vapi. Vapi can use BYO SIP trunk credentials and BYO phone numbers; outbound-only trunks can use a hostname/IP gateway, while inbound gateways require numeric IP matching.",
      inboundTitle: "Vendor receives inbound calls in Vapi",
      outboundTitle: "Vapi places outbound calls through Meydan",
      inboundRequirement: "Create/register BYO phone numbers in Vapi and configure inbound SIP routing according to Vapi's SIP trunk rules.",
      outboundRequirement: "Create a BYO SIP trunk credential, then call with assistantId and phoneNumberId.",
      note:
        "For Vapi, give the vendor the numbers, Meydan public IP, TLS gateway details, and remind them that inbound-enabled gateways require a numeric IPv4 gateway."
    };
  }
  if (provider === "generic_sip") {
    return {
      label: "Generic SIP / other",
      summary:
        "Use this for any vendor that can connect a DID to a SIP trunk or register a SIP/PSTN phone number. The vendor must confirm the exact SIP URI, auth method, codecs, and media encryption support.",
      inboundTitle: "Vendor receives inbound calls",
      outboundTitle: "Vendor places outbound calls through Meydan",
      inboundRequirement: "Vendor must provide or accept a SIP ingress path for the listed DIDs.",
      outboundRequirement: "Vendor must support TLS SIP gateway dialing to Meydan public IP.",
      note:
        "For non-ElevenLabs vendors, treat this as the technical handoff checklist. Confirm their platform-specific phone number ID, assistant ID, and SIP authentication requirements before going live."
    };
  }
  return {
    label: "ElevenLabs",
    summary:
      "Use this when the vendor uses ElevenLabs Conversational AI phone numbers and agents. The handoff maps each DID to an ElevenLabs agent/phone number and Meydan's SIP trunk settings.",
    inboundTitle: "ElevenLabs receives inbound calls",
    outboundTitle: "ElevenLabs agent dials through Meydan",
    inboundRequirement: "Configure the ElevenLabs SIP phone number for the listed DIDs.",
    outboundRequirement: "Use Meydan public IP over TLS with media encryption disabled.",
    note:
      "For ElevenLabs, create or update the phone number, assign the correct agent ID, then run inbound and outbound tests before marking live."
  };
}

function buildHandoffPackage(rows: DidInventoryItem[], provider: HandoffProvider) {
  const numbers = rows.map((row) => ({
    did: row.did,
    e164: row.e164,
    status: row.status,
    company: row.assigned_user?.company_name || row.assigned_user?.name || null,
    vendor: row.vendor_profile?.vendor_name || null,
    assistant_profile: row.assistant_profile?.type || null,
    assistant_or_agent_id: row.assigned_agent_id || null,
    provider_phone_number_id: row.elevenlabs_phone_number_id || null,
    handoff_notes: row.vendor_profile?.handoff_notes || row.notes || null
  }));

  const inbound = {
    numbers: rows.map((row) => row.e164),
    meydan_source_ip_allowlist: ["5.32.91.6/32"],
    media_encryption: "disabled",
    codecs: ["ulaw", "alaw"],
    provider_specific:
      provider === "vapi"
        ? {
            provider: "vapi",
            setup: "Create BYO phone-number resources and attach them to the vendor assistant/trunk.",
            warning: "Inbound-enabled Vapi gateways require numeric IPv4 gateway matching."
          }
        : provider === "elevenlabs"
          ? {
              provider: "elevenlabs",
              setup: "Create or update Conversational AI SIP phone numbers and assign the correct agent."
            }
          : {
              provider: "generic_sip",
              setup: "Vendor must provide the platform-specific SIP ingress and number association steps."
            }
  };

  const outbound = {
    numbers: rows.map((row) => row.e164),
    meydan_gateway: {
      address: "5.32.91.6",
      port: 5061,
      transport: "tls",
      media_encryption: "disabled",
      codecs: ["ulaw", "alaw"]
    },
    provider_specific:
      provider === "vapi"
        ? {
            provider: "vapi",
            credential_type: "byo-sip-trunk",
            call_test: "POST /call/phone with assistantId, customer.number, and phoneNumberId."
          }
        : provider === "elevenlabs"
          ? {
              provider: "elevenlabs",
              phone_number_type: "sip_trunk",
              call_test: "Start an outbound call using the assigned agent phone number ID."
            }
          : {
              provider: "generic_sip",
              call_test: "Vendor must confirm outbound API/dashboard test method."
            }
  };

  return {
    version: "vendor-handoff-v2",
    generated_at: new Date().toISOString(),
    provider,
    provider_label: providerCopyFor(provider).label,
    numbers,
    inbound,
    outbound
  };
}

function buildVendorInstructions(rows: DidInventoryItem[], provider: HandoffProvider) {
  const providerCopy = providerCopyFor(provider);
  const lines = [
    `Provider: ${providerCopy.label}`,
    `Numbers: ${rows.map((row) => row.e164).join(", ")}`,
    "",
    "Meydan SIP details:",
    "- Public IP: 5.32.91.6",
    "- SIP TLS port: 5061",
    "- Media encryption: disabled",
    "- Codecs: ulaw, alaw",
    "- Inbound source allowlist: 5.32.91.6/32",
    "",
    "Number mapping:"
  ];

  for (const row of rows) {
    lines.push(
      `- ${row.did} / ${row.e164}: company=${row.assigned_user?.company_name || row.assigned_user?.name || "not set"}, assistant_or_agent_id=${row.assigned_agent_id || "not set"}, provider_phone_number_id=${row.elevenlabs_phone_number_id || "not set"}`
    );
  }

  lines.push("", "Provider notes:", providerCopy.note);

  if (provider === "vapi") {
    lines.push(
      "",
      "Vapi checklist:",
      "- Create BYO SIP trunk credential.",
      "- Create BYO phone-number resource for each DID.",
      "- For outbound, test with assistantId + phoneNumberId.",
      "- For inbound, use numeric IPv4 gateway matching if inbound gateway is enabled."
    );
  }

  return lines.join("\n");
}

function firstValue(rows: DidInventoryItem[], pick: (row: DidInventoryItem) => string | undefined) {
  for (const row of rows) {
    const value = pick(row);
    if (value) return value;
  }
  return undefined;
}

function formFromItem(item: DidInventoryItem): AssignmentForm {
  return {
    status: item.status,
    company_name: item.assigned_user?.company_name || "",
    customer_name: item.assigned_user?.name || "",
    customer_email: item.assigned_user?.email || "",
    crm_contact_id: item.assigned_user?.crm_contact_id || "",
    customer_notes: item.assigned_user?.notes || "",
    agent_id: item.assigned_agent_id || "",
    assistant_type: item.assistant_profile?.type || "meydan_operator",
    assistant_display_name: item.assistant_profile?.display_name || "",
    assistant_owner: item.assistant_profile?.owner || "",
    assistant_notes: item.assistant_profile?.notes || "",
    vendor_name: item.vendor_profile?.vendor_name || "",
    vendor_contact_email: item.vendor_profile?.contact_email || "",
    pa_dashboard_customer_id: item.vendor_profile?.pa_dashboard_customer_id || "",
    elevenlabs_account_name: item.vendor_profile?.elevenlabs_account_name || "",
    elevenlabs_account_owner_email: item.vendor_profile?.elevenlabs_account_owner_email || "",
    handoff_notes: item.vendor_profile?.handoff_notes || "",
    current_provider: item.vendor_profile?.current_provider || "",
    elevenlabs_phone_number_id: item.elevenlabs_phone_number_id || "",
    elevenlabs_status: item.elevenlabs_status,
    last_test_result: item.last_test_result,
    notes: item.notes || ""
  };
}

type DidInventoryPatch = Partial<DidInventoryItem>;

function patchFromForm(form: AssignmentForm): DidInventoryPatch {
  const hasAssignedUser = Boolean(
    form.company_name.trim() ||
      form.customer_name.trim() ||
      form.customer_email.trim() ||
      form.crm_contact_id.trim() ||
      form.customer_notes.trim()
  );
  const hasVendor = Boolean(
    form.vendor_name.trim() ||
      form.vendor_contact_email.trim() ||
      form.pa_dashboard_customer_id.trim() ||
      form.elevenlabs_account_name.trim() ||
      form.elevenlabs_account_owner_email.trim() ||
      form.handoff_notes.trim() ||
      form.current_provider
  );
  return {
    status: form.status,
    assigned_user: hasAssignedUser
      ? {
          company_name: form.company_name.trim() || undefined,
          name: form.customer_name.trim() || form.company_name.trim() || "Unassigned contact",
          email: form.customer_email.trim(),
          crm_contact_id: form.crm_contact_id.trim() || undefined,
          notes: form.customer_notes.trim() || undefined
        }
      : null,
    assigned_agent_id: form.agent_id.trim() || undefined,
    assistant_profile: {
      type: form.assistant_type,
      display_name: form.assistant_display_name.trim() || productTemplates[form.assistant_type].display_name,
      owner: form.assistant_owner.trim() || productTemplates[form.assistant_type].owner,
      notes: form.assistant_notes.trim() || productTemplates[form.assistant_type].notes
    },
    vendor_profile: hasVendor
      ? {
          vendor_name: form.vendor_name.trim() || undefined,
          contact_email: form.vendor_contact_email.trim() || undefined,
          pa_dashboard_customer_id: form.pa_dashboard_customer_id.trim() || undefined,
          elevenlabs_account_name: form.elevenlabs_account_name.trim() || undefined,
          elevenlabs_account_owner_email: form.elevenlabs_account_owner_email.trim() || undefined,
          handoff_notes: form.handoff_notes.trim() || undefined,
          current_provider: form.current_provider || undefined,
          target_provider: "elevenlabs_sip"
        }
      : undefined,
    elevenlabs_phone_number_id: form.elevenlabs_phone_number_id.trim() || undefined,
    elevenlabs_status: form.elevenlabs_status,
    last_test_result: form.last_test_result,
    notes: form.notes.trim() || undefined
  };
}

function applyTemplate(form: AssignmentForm, templateKey: string): AssignmentForm {
  const template = productTemplates[templateKey] || productTemplates.meydan_operator;
  return {
    ...form,
    assistant_type: template.type,
    assistant_display_name: template.display_name || "",
    assistant_owner: template.owner || "",
    assistant_notes: template.notes || "",
    vendor_name: template.type === "vendor_pa" ? form.vendor_name : form.vendor_name
  };
}

function label(value: string) {
  return value.replaceAll("_", " ");
}

function formatDate(value: string | undefined) {
  if (!value) return "N/A";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

createRoot(document.getElementById("root")!).render(<App />);
